function trimTrailingSlash(url) {
  return url.trim().replace(/\/+$/, '');
}

export const APP_PATHS = {
  trialGallery: '/trial-gallery',
  eventJoin: '/join',
  signIn: '/sign-in',
  signUp: '/sign-up',
  subscriptions: '/api/v1/subscriptions',
  eventJoinPreview: '/api/v1/public/events/join',
};

export function getAppBaseUrl() {
  const raw = process.env.NEXT_PUBLIC_APP_URL;
  if (!raw?.trim()) {
    throw new Error('NEXT_PUBLIC_APP_URL is not set. Add it to your .env file.');
  }
  return trimTrailingSlash(raw);
}

/**
 * No fallback to NEXT_PUBLIC_APP_URL. It used to fall back, but `next.config.mjs`
 * never did — so one missing variable sent the pricing fetch to the app host
 * (which answers /api/* with HTML) while the event-join rewrite vanished
 * entirely. Two different silent failures from one cause. Both resolvers now
 * read this variable and nothing else.
 */
export function getApiBaseUrl() {
  const raw = process.env.NEXT_PUBLIC_API_URL;
  if (!raw?.trim()) {
    throw new Error(
      'NEXT_PUBLIC_API_URL is not set. It must be the api.* host — the app host serves HTML for /api/* paths.',
    );
  }
  return trimTrailingSlash(raw);
}

export function appPath(path) {
  const normalizedPath = path.startsWith('/') ? path : `/${path}`;
  return `${getAppBaseUrl()}${normalizedPath}`;
}

export function getTrialGalleryUrl() {
  const explicitTrialUrl = process.env.NEXT_PUBLIC_FREE_TRIAL_URL?.trim();
  if (explicitTrialUrl) {
    return trimTrailingSlash(explicitTrialUrl);
  }
  return appPath(APP_PATHS.trialGallery);
}

/**
 * Marks a join link as coming from this site's Event ID box. The app uses it
 * to tell a landing-page guest apart from someone who signed up directly with
 * no plan — only the former gets the locked workspace and upsell card.
 */
export const EVENT_ACCESS_SOURCE = 'event-access';

export function getEventJoinUrl(eventCode, { fromEventAccess = false } = {}) {
  const code = String(eventCode ?? '').trim();
  if (!code) return '';
  const path = `${APP_PATHS.eventJoin}/${encodeURIComponent(code)}`;
  const url = appPath(path);
  return fromEventAccess ? `${url}?src=${EVENT_ACCESS_SOURCE}` : url;
}

/**
 * Public event-join preview. Same-origin path — next.config rewrites it to
 * the API host so the browser is not subject to API CORS.
 */
export function getEventJoinPreviewUrl(eventCode) {
  const code = String(eventCode ?? '').trim();
  if (!code) return '';
  return `${APP_PATHS.eventJoinPreview}/${encodeURIComponent(code)}`;
}

export function getSignInUrl() {
  return appPath(APP_PATHS.signIn);
}

export function getSignUpPageUrl() {
  return appPath(APP_PATHS.signUp);
}

export function getSignUpUrl({ planId, billingCycle, segment }) {
  const params = new URLSearchParams({ planId, billingCycle, segment });
  return `${getSignUpPageUrl()}?${params.toString()}`;
}

export function getSubscriptionsUrl({ segment, platform = 'web' }) {
  const params = new URLSearchParams({ segment, platform });
  return `${getApiBaseUrl()}${APP_PATHS.subscriptions}/?${params.toString()}`;
}
